import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import {
  projectEssentialApi,
  type AnnouncementProjectOption,
  type ProjectAnnouncementItem,
} from '../services/api';

type AnnouncementTab = 'latest' | 'all' | 'bounceOut' | 'lastFewUnits';

const PAGE_SIZE = 10;

interface ProjectAnnouncementsDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Project-scoped mode (detail page). When omitted, the dialog shows all
   * published project announcements with an optional project filter.
   */
  projectId?: string | null;
  projectName?: string | null;
}

function formatCreatedAt(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Announcement.Content is nvarchar that may mix HTML (`<br>`, `<a>`, `<img>`, …)
 * with WhatsApp-style markers (`*bold*`, `_italic_`, `~strike~`). Convert
 * those markers and normalize line breaks so the detail view reads cleanly.
 *
 * Important: never rewrite URLs that already live inside HTML attributes
 * (`href`, `src`, …) — that produces the "Open Link style=…>" leak.
 */
function formatAnnouncementHtml(raw: string): string {
  if (!raw) return '';

  const tags: string[] = [];
  let formatted = String(raw)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // Protect existing HTML tags so markup conversion doesn't touch attrs.
    .replace(/<[^>]+>/g, (tag) => {
      tags.push(tag);
      return `\0TAG${tags.length - 1}\0`;
    });

  // WhatsApp-style emphasis (common in project blast copy).
  formatted = formatted
    .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
    .replace(/(^|[^A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9])/g, '$1<em>$2</em>')
    .replace(/~([^~\n]+)~/g, '<s>$1</s>');

  // Decorative bullet/dot separators → horizontal rule.
  formatted = formatted.replace(
    /(?:^|\n)[ \t]*[•·●.]{6,}[ \t]*(?=\n|$)/g,
    '\n<hr class="announcement-rule" />\n',
  );

  // Restore HTML tags.
  formatted = formatted.replace(/\0TAG(\d+)\0/g, (_, index) => tags[Number(index)] ?? '');

  // Preserve plain newlines when the payload isn't already break-structured.
  if (!/<(br|p|div|li|tr|h[1-6])\b/i.test(formatted)) {
    formatted = formatted.replace(/\n{2,}/g, '<br /><br />').replace(/\n/g, '<br />');
  } else if (/\n/.test(formatted)) {
    formatted = formatted.replace(/\n/g, '<br />');
  }

  // Enhance real anchors (multiline bodies allowed). Keep original label —
  // titles/emoji/images inside the link should not become "Open Link".
  formatted = formatted.replace(/<a\s+([^>]*?)>([\s\S]*?)<\/a>/gi, (_match, attrs, label) => {
    let newAttrs = String(attrs || '');
    if (!/\btarget\s*=/i.test(newAttrs)) newAttrs += ' target="_blank"';
    if (!/\brel\s*=/i.test(newAttrs)) newAttrs += ' rel="noopener noreferrer"';
    const text = String(label ?? '');
    return `<a ${newAttrs}>${text.trim() ? text : 'Open Link'}</a>`;
  });

  // Linkify bare URLs in text nodes only (never inside tags/attributes).
  formatted = formatted.replace(/(<[^>]+>)|([^<]+)/g, (segment, tag, text) => {
    if (tag) return tag;
    return String(text).replace(/https?:\/\/[^\s<]+/gi, (url) => {
      const trimmed = url.replace(/[),.;:!?"'》】）]+$/g, '');
      const trailing = url.slice(trimmed.length);
      const cleaned = trimmed.replace(/["'>]+$/g, '');
      const leftover = trimmed.slice(cleaned.length);
      if (!cleaned) return url;
      return `<a href="${cleaned}" target="_blank" rel="noopener noreferrer">Open Link</a>${leftover}${trailing}`;
    });
  });

  return formatted;
}

function matchesTab(item: ProjectAnnouncementItem, tab: AnnouncementTab): boolean {
  if (tab === 'latest' || tab === 'all') return true;
  const sub = (item.subCategory || '').trim().toLowerCase();
  if (tab === 'bounceOut') return sub === 'bounceout';
  if (tab === 'lastFewUnits') return sub === 'lastfewunits';
  return true;
}

export default function ProjectAnnouncementsDialog({
  open,
  onClose,
  projectId = null,
  projectName,
}: ProjectAnnouncementsDialogProps) {
  const isGlobal = !projectId;
  const [tab, setTab] = useState<AnnouncementTab>('latest');
  const [items, setItems] = useState<ProjectAnnouncementItem[]>([]);
  const [projectOptions, setProjectOptions] = useState<AnnouncementProjectOption[]>([]);
  const [filterProjectId, setFilterProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      setTab('latest');
      setSelectedId(null);
      setError(null);
      setFilterProjectId('');
      setPage(1);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        if (isGlobal) {
          const [announcementsRes, projectsRes] = await Promise.all([
            projectEssentialApi.getAnnouncements(),
            projectEssentialApi.getAnnouncementProjects(),
          ]);
          if (!mounted) return;
          setItems(announcementsRes.items || []);
          setProjectOptions(projectsRes.items || []);
        } else {
          const res = await projectEssentialApi.getProjectAnnouncements(projectId!);
          if (!mounted) return;
          setItems(res.items || []);
          setProjectOptions([]);
        }
      } catch (err: unknown) {
        if (!mounted) return;
        setItems([]);
        setProjectOptions([]);
        setError(err instanceof Error ? err.message : 'Failed to load announcements');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, [open, projectId, isGlobal]);

  const scopedItems = useMemo(() => {
    let list = items;
    if (isGlobal && filterProjectId) {
      list = list.filter((item) => item.projectId === filterProjectId);
    }
    return list;
  }, [items, isGlobal, filterProjectId]);

  const filteredItems = useMemo(() => {
    if (tab === 'latest') {
      // Newest first from the API — keep a single page of results.
      return scopedItems.slice(0, PAGE_SIZE);
    }
    return scopedItems.filter((item) => matchesTab(item, tab));
  }, [scopedItems, tab]);

  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedItems = useMemo(() => {
    if (tab === 'latest') return filteredItems;
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, safePage, tab]);

  useEffect(() => {
    setPage(1);
  }, [tab, filterProjectId, open, projectId]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const selected = useMemo(
    () => (selectedId ? items.find((item) => item.id === selectedId) ?? null : null),
    [items, selectedId],
  );

  useEffect(() => {
    const root = contentRef.current;
    if (!root || !selected) return;
    const images = Array.from(root.querySelectorAll('img'));
    const onError = (event: Event) => {
      const img = event.currentTarget as HTMLImageElement;
      img.style.display = 'none';
    };
    images.forEach((img) => {
      if (img.complete && img.naturalWidth === 0) {
        img.style.display = 'none';
        return;
      }
      img.addEventListener('error', onError);
    });
    return () => {
      images.forEach((img) => img.removeEventListener('error', onError));
    };
  }, [selected]);

  const tabCounts = useMemo(
    () => ({
      all: scopedItems.length,
      bounceOut: scopedItems.filter((item) => matchesTab(item, 'bounceOut')).length,
      lastFewUnits: scopedItems.filter((item) => matchesTab(item, 'lastFewUnits')).length,
    }),
    [scopedItems],
  );

  const selectedFilterProject = useMemo(
    () => projectOptions.find((project) => project.id === filterProjectId) ?? undefined,
    [projectOptions, filterProjectId],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface-elevated-strong)',
          color: 'var(--color-text)',
          backgroundImage: 'none',
          border: { sm: '1px solid var(--color-border)' },
          borderRadius: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: '92vw' },
          maxWidth: { sm: 920 },
          height: { sm: '92vh' },
          maxHeight: { sm: '92vh' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {selected ? (
          <IconButton
            onClick={() => setSelectedId(null)}
            aria-label="Back to announcements list"
            edge="start"
            sx={{ color: 'var(--color-text-strong)', mr: 0.5 }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : null}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 600,
              fontSize: '0.7rem',
              display: 'block',
            }}
          >
            {selected ? 'Announcement' : 'Announcements'}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'var(--color-text-strong)',
              lineHeight: 1.2,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {selected
              ? selected.title
              : isGlobal
                ? 'All Projects'
                : projectName || projectId}
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          aria-label="Close announcements"
          sx={{ color: 'var(--color-text-strong)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {!selected ? (
        <Box
          sx={{
            borderBottom: '1px solid var(--color-border)',
            bgcolor: 'var(--color-surface-elevated)',
            px: { xs: 1, sm: 2 },
            pt: isGlobal ? 1.5 : 0,
          }}
        >
          {isGlobal ? (
            <Box sx={{ mb: 1 }}>
              <Autocomplete
                size="small"
                options={projectOptions}
                value={selectedFilterProject}
                onChange={(_e, next) => {
                  setFilterProjectId(next?.id ?? '');
                  setTab('latest');
                  setPage(1);
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disableClearable
                noOptionsText="No matching projects"
                ListboxProps={{ style: { maxHeight: 360 } }}
                componentsProps={{
                  paper: {
                    sx: {
                      bgcolor: 'var(--color-surface-elevated-strong)',
                      border: '1px solid var(--color-border)',
                      '& .MuiAutocomplete-option': { color: 'var(--color-text-strong)' },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    placeholder="Search projects"
                    autoComplete="off"
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'var(--color-surface)',
                        color: 'var(--color-text-strong)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--color-border-strong)',
                      },
                    }}
                  />
                )}
              />
              {filterProjectId ? (
                <Button
                  size="small"
                  onClick={() => {
                    setFilterProjectId('');
                    setTab('latest');
                    setPage(1);
                  }}
                  sx={{
                    mt: 0.5,
                    px: 0.5,
                    minWidth: 0,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'var(--color-accent-strong)',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Clear Search
                </Button>
              ) : null}
            </Box>
          ) : null}
          <Tabs
            value={tab}
            onChange={(_e, value: AnnouncementTab) => {
              setTab(value);
              setPage(1);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.78rem', sm: '0.85rem' },
                color: 'var(--color-text-muted)',
                minHeight: 44,
                gap: 0.75,
                '&.Mui-selected': { color: 'var(--color-accent-strong)' },
              },
              '& .MuiTab-iconWrapper': {
                marginBottom: '0 !important',
                marginRight: 0,
              },
              '& .MuiTabs-indicator': { bgcolor: 'var(--color-accent)' },
            }}
          >
            <Tab
              value="latest"
              icon={<NewReleasesOutlinedIcon fontSize="small" />}
              iconPosition="start"
              label="Latest"
            />
            <Tab value="all" label={`All (${tabCounts.all})`} />
            <Tab value="bounceOut" label={`Bounce Out (${tabCounts.bounceOut})`} />
            <Tab value="lastFewUnits" label={`Last Few Units (${tabCounts.lastFewUnits})`} />
          </Tabs>
        </Box>
      ) : null}

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 3, textAlign: 'center' }}>
            {error}
          </Typography>
        ) : selected ? (
          <Stack spacing={1.5}>
            <Typography
              variant="caption"
              sx={{ color: 'var(--color-text-muted)', fontWeight: 600 }}
            >
              {formatCreatedAt(selected.createdAt)}
              {selected.projectName ? ` · ${selected.projectName}` : ''}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                color: 'var(--color-text-strong)',
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                lineHeight: 1.3,
              }}
            >
              {selected.title}
            </Typography>
            <Box
              ref={contentRef}
              sx={{
                color: 'var(--color-text)',
                fontSize: { xs: '0.92rem', sm: '0.98rem' },
                lineHeight: 1.65,
                wordBreak: 'break-word',
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
                '& video, & .video-avatar': {
                  display: 'block',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  // Portrait reels otherwise dominate mobile; cap height hard.
                  maxHeight: { xs: '42vh', sm: 360 },
                  objectFit: 'contain',
                  borderRadius: 1.5,
                  my: 1.5,
                  mx: 'auto',
                  backgroundColor: '#000',
                },
                '& p': { m: 0, mb: 1.25 },
                '& p:last-child': { mb: 0 },
                '& ul, & ol': { pl: 2.5, mb: 1.25, mt: 0 },
                '& li': { mb: 0.5 },
                '& strong, & b': {
                  fontWeight: 800,
                  color: 'var(--color-text-strong)',
                },
                '& em, & i': {
                  fontStyle: 'italic',
                  color: 'var(--color-text)',
                },
                '& s': {
                  color: 'var(--color-text-muted)',
                },
                '& a': {
                  color: 'var(--color-accent)',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  wordBreak: 'break-all',
                },
                '& hr.announcement-rule, & hr': {
                  border: 0,
                  borderTop: '1px solid var(--color-border-strong)',
                  my: 1.75,
                },
              }}
              dangerouslySetInnerHTML={{ __html: formatAnnouncementHtml(selected.content) }}
            />
          </Stack>
        ) : filteredItems.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: { xs: 4, sm: 6 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent-strong)',
              }}
            >
              <CampaignOutlinedIcon />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
              No announcements
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', maxWidth: 320 }}>
              There are currently no published announcements in this category
              {filterProjectId ? ' for the selected project' : ''}.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            {pagedItems.map((item) => (
              <ButtonBase
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  px: 1.5,
                  py: 1.5,
                  borderRadius: 1.5,
                  border: '1.5px solid var(--color-border-strong)',
                  bgcolor: 'var(--color-surface)',
                  color: 'var(--color-text-strong)',
                  transition: 'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                  '&:hover, &:focus-visible': {
                    bgcolor: 'var(--color-accent-soft)',
                    borderColor: 'var(--color-accent)',
                    boxShadow: '0 0 0 1px var(--color-accent) inset',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    mr: 1.25,
                    mt: 0.15,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'var(--color-accent-soft)',
                    color: 'var(--color-accent-strong)',
                    flexShrink: 0,
                  }}
                >
                  <CampaignOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      lineHeight: 1.35,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.35,
                      color: 'var(--color-text-muted)',
                      fontWeight: 600,
                    }}
                  >
                    {formatCreatedAt(item.createdAt)}
                    {isGlobal && item.projectName ? ` · ${item.projectName}` : ''}
                  </Typography>
                </Box>
                <ChevronRightIcon
                  sx={{ color: 'var(--color-text-muted)', flexShrink: 0, mt: 0.5 }}
                />
              </ButtonBase>
            ))}
            {pageCount > 1 ? (
              <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                <Pagination
                  count={pageCount}
                  page={safePage}
                  onChange={(_e, next) => setPage(next)}
                  shape="rounded"
                  siblingCount={1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'var(--color-text-strong)',
                      borderColor: 'var(--color-border-strong)',
                      fontWeight: 600,
                      '&.Mui-selected': {
                        bgcolor: 'var(--color-accent)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                      },
                      '&:hover': { bgcolor: 'var(--color-accent-soft)' },
                    },
                  }}
                />
              </Stack>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
